import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import classNames from 'classnames';

import { StaticImage } from '@nxte-nl/static-image';

import { RedeemLinkContainer } from '../../containers/RedeemLinkContainer/RedeemLinkContainer';
import { cookieHooks } from '../../hooks/cookieHooks/cookieHooks';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { versionDependent } from '../../images/versionDependentExport';
import { LayoutExtension } from '../../types/ResponsiveState';
import { ImagePresentational } from '../../types/common';
import {
  FooterBottomPartSocialIcons,
} from '../FooterBottomPartSocialIcons/FooterBottomPartSocialIcons';
import { FooterCopyright } from '../FooterCopyright/FooterCopyright';
import { LinkIntent, Link, LinkType, LinkTextTransform, LinkTarget } from '../Link/Link';
import styles from './FooterBottomPart.module.css';

const messages = defineMessages({
  termsAndConditions: {
    id: 'src.components.FooterBottomPart.termsAndConditions',
    defaultMessage: 'Terms And Conditions',
  },
  privacy: {
    id: 'src.components.FooterBottomPart.privacy',
    defaultMessage: 'Privacy',
  },
  disclaimer: {
    id: 'src.components.FooterBottomPart.disclaimer',
    defaultMessage: 'Disclaimer',
  },
  footerLogoAlt: {
    id: 'src.components.FooterBottomPart.footerLogoAlt',
    defaultMessage: 'Footer logo',
  },
});

export type Props = {
  footerLogoFromCBE?: ImagePresentational | null;
  isCookiesFooterLink?: boolean;
  isLogosFromCBEOn: boolean;
  isRedeem?: boolean;
  layoutExtension: LayoutExtension;
};

export const FooterBottomPart = React.memo((props: Props) => {
  const {
    isRedeem = false,
    layoutExtension,
    footerLogoFromCBE,
    isLogosFromCBEOn,
    isCookiesFooterLink = false,
  } = props;
  const intl = useIntl();

  const redirectToPrivacyPage = navigationHooks.usePrivacyPageRedirect();
  const redirectToTermsPage = navigationHooks.useTermsAndConditionsPageRedirect();

  const redirectToRedeemPrivacyPage = navigationHooks.useRedeemPrivacyPageRedirect(isRedeem);
  const redirectToRedeemTermsPage = navigationHooks.
    useRedeemTermsAndConditionsPageRedirect(isRedeem);

  const onOpenCookieModalHandler = cookieHooks.useCookieModal();

  return (
    <section className={styles.wrapper}>
      <div className={styles.footerBottomPart}>
        <div className={styles.footerBottomPartInnerWrapper}>
          <section className={styles.logoAndLinksContainer}>
            <StaticImage
              className={styles.logo}
              src={isLogosFromCBEOn && footerLogoFromCBE?.url.href
                ? footerLogoFromCBE?.url.href
                : versionDependent.footerLogo.src}
              alt={isLogosFromCBEOn && footerLogoFromCBE?.alt
                ? footerLogoFromCBE?.alt
                : intl.formatMessage(messages.footerLogoAlt)}
              width={48}
              height={48}
            />

            <div className={styles.linksContainer}>
              {isRedeem
                ? <>
                  <RedeemLinkContainer
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    className={styles.link}
                    redirect={redirectToTermsPage()}
                    reserveRedirect={redirectToRedeemTermsPage()}
                  >
                    <FormattedMessage {...messages.termsAndConditions} />
                  </RedeemLinkContainer>

                  <RedeemLinkContainer
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    className={styles.link}
                    redirect={redirectToPrivacyPage()}
                    reserveRedirect={redirectToRedeemPrivacyPage()}
                  >
                    <FormattedMessage {...messages.privacy} />
                  </RedeemLinkContainer>

                  <RedeemLinkContainer
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    className={classNames(styles.link, styles.hiddenLink)}
                    redirect='#'
                  >
                    <FormattedMessage {...messages.disclaimer} />
                  </RedeemLinkContainer>
                </>

                : <>
                  <Link
                    type={LinkType.Href}
                    target={LinkTarget.Blank}
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    to={redirectToTermsPage}
                    className={styles.link}
                  >
                    <FormattedMessage {...messages.termsAndConditions} />
                  </Link>

                  <Link
                    type={LinkType.Href}
                    target={LinkTarget.Blank}
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    to={redirectToPrivacyPage}
                    className={styles.link}
                  >
                    <FormattedMessage {...messages.privacy} />
                  </Link>

                  {isCookiesFooterLink
                    && <Link
                      type={LinkType.Span}
                      onClick={onOpenCookieModalHandler}
                      intent={LinkIntent.tertiary}
                      textTransform={LinkTextTransform.Uppercase}
                      className={styles.link}
                    >
                      Cookies
                    </Link>}

                  <Link
                    type={LinkType.Href}
                    target={LinkTarget.Blank}
                    intent={LinkIntent.tertiary}
                    textTransform={LinkTextTransform.Uppercase}
                    to={'#'}
                    className={classNames(styles.link, styles.hiddenLink)}
                  >
                    <FormattedMessage {...messages.disclaimer} />
                  </Link>
                </>
              }
            </div>

            {(layoutExtension === LayoutExtension.Mobile
              || layoutExtension === LayoutExtension.Tablet)
              ? <FooterCopyright className={styles.footerCopyright} />
              : null}
          </section>

          <section className={styles.socialIconsWithLinksContainer}>
            {versionDependent.socialLinks.length > 0
              && <FooterBottomPartSocialIcons layoutExtension={layoutExtension}/>
            }

            {layoutExtension === LayoutExtension.DesktopLarge
              ? <FooterCopyright className={styles.footerCopyright} />
              : null}
          </section>
        </div>

        {layoutExtension === LayoutExtension.DesktopSmall
          ? <FooterCopyright className={styles.footerCopyright} />
          : null}
      </div>
    </section>
  );
});

FooterBottomPart.displayName = 'FooterBottomPart';
